import {
  HANDOVER_REVERSED_MESSAGE,
  handoverOverlapMessage,
  handoverPassedMessage,
  type HandoverDay,
  type HandoverDayRule,
} from '@bbbk/schema';

// Account › Customer portal › Restricted drop-off and collection days
// (handover-days) — the card's rules, kept out of the component so they can be
// pinned directly (tests/unit/handover-days.test.ts), as sizeLimits.ts does for
// the card beside it. The refusal sentences are @bbbk/schema's: the route says
// the same ones if a request gets past the card.

/** The weekday keys `handover_closed_weekly` stores, Monday first as the list reads. */
export const WEEKDAYS = [
  ['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'],
  ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday'],
] as const;
export type WeekdayKey = (typeof WEEKDAYS)[number][0];

/** A weekday's rule. `am_only` is never offered, but a stored one stays visible so it can be removed. */
export interface WeeklyRow {
  key: WeekdayKey;
  day: string;
  rule: HandoverDayRule | 'am_only';
}

export interface Draft {
  mode: 'dates' | 'weekly';
  from: string;
  to: string;
  weekday: WeekdayKey;
  rule: HandoverDayRule;
  note: string;
}
export const EMPTY_DRAFT: Draft = { mode: 'dates', from: '', to: '', weekday: 'sun', rule: 'pm_only', note: '' };

export type DraftField = 'from' | 'to' | 'weekday';
export interface DraftError {
  field: DraftField;
  message: string;
}

/** AC1 — today onwards, oldest first. A row that ends today still matters today. */
export function upcoming(rows: readonly HandoverDay[], today: string): HandoverDay[] {
  return rows.filter((r) => r.endsOn >= today).sort((a, b) => (a.startsOn < b.startsOn ? -1 : a.startsOn > b.startsOn ? 1 : 0));
}

/** The weekly setting as rows: `sun:am` is afternoon only, both halves closed. */
export function weeklyRows(closedWeekly: readonly string[]): WeeklyRow[] {
  const out: WeeklyRow[] = [];
  for (const [key, day] of WEEKDAYS) {
    const am = closedWeekly.includes(`${key}:am`);
    const pm = closedWeekly.includes(`${key}:pm`);
    if (am && pm) out.push({ key, day, rule: 'closed' });
    else if (am) out.push({ key, day, rule: 'pm_only' });
    else if (pm) out.push({ key, day, rule: 'am_only' });
  }
  return out;
}

/** The setting with a weekday's rule added (AC5). */
export function withWeekly(closedWeekly: readonly string[], key: WeekdayKey, rule: HandoverDayRule): string[] {
  return [...closedWeekly, ...(rule === 'closed' ? [`${key}:am`, `${key}:pm`] : [`${key}:am`])];
}

/** The setting with every half of a weekday taken out (AC6). */
export function withoutWeekly(closedWeekly: readonly string[], key: WeekdayKey): string[] {
  return closedWeekly.filter((x) => !x.startsWith(`${key}:`));
}

/**
 * What Add refuses before anything is sent (AC3, AC4, AC5) — the same refusals
 * the route makes, so the card never shows one sentence and the server another.
 */
export function checkDraft(d: Draft, rows: readonly HandoverDay[], closedWeekly: readonly string[], today: string): DraftError | null {
  if (d.mode === 'weekly') {
    const taken = weeklyRows(closedWeekly).find((w) => w.key === d.weekday);
    return taken ? { field: 'weekday', message: `${taken.day}s already have a rule. Remove it first to change it.` } : null;
  }
  if (!d.from) return { field: 'from', message: 'Pick the first day.' };
  const to = d.to || d.from;
  if (d.from < today) return { field: 'from', message: handoverPassedMessage(d.from) };
  if (to < d.from) return { field: 'to', message: HANDOVER_REVERSED_MESSAGE };
  const clash = rows.find((r) => d.from <= r.endsOn && r.startsOn <= to);
  if (!clash) return null;
  // Mark the end that ran in: the first day when it lands inside the row,
  // otherwise the last.
  const field: DraftField = d.from >= clash.startsOn && d.from <= clash.endsOn ? 'from' : 'to';
  return { field, message: handoverOverlapMessage(clash) };
}
